import { createFileRoute } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";

import type { GoalTimeline } from "~/../drizzle/schema";
import { EncryptionBadge } from "~/components/encryption-badge";
import { ErrorStateCard } from "~/components/error-state-card";
import { Button } from "~/components/ui/button";
import { EmptyState } from "~/components/ui/empty-state";
import { GOAL_TIMELINE_OPTIONS } from "~/components/ui/goal-badges";
import { useUnlockedKey } from "~/lib/crypto";
import { useGoalsOfLife } from "~/lib/queries/goalOfLifeQuery";
import { useUserSharedStatus } from "~/lib/queries/userSharedQuery";

import {
  countOpenGoals,
  goalItems,
  groupGoals,
  type GoalItem,
} from "./-components/goal-groups";
import { GoalList } from "./-components/goal-list";
import { GoalsEmptyState } from "./-components/goals-empty-state";
import { GoalsSkeleton } from "./-components/goals-skeleton";
import { SharingMenu } from "./-components/sharing-menu";
import { useGoalPanels } from "./-components/use-goal-panels";

export const Route = createFileRoute("/_user/life-design/goals")({
  staticData: { title: "Goals" },
  component: RouteComponent,
});

function TimeframeChips({
  items,
  selected,
  onSelect,
}: {
  items: GoalItem[];
  selected: GoalTimeline | undefined;
  onSelect: (timeline: GoalTimeline | undefined) => void;
}) {
  const options = [
    { id: undefined, label: "All" },
    ...GOAL_TIMELINE_OPTIONS,
  ] as const;
  return (
    // The chips scroll in their own row on a phone, never the page.
    <div
      role="group"
      aria-label="Timeframe"
      className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
    >
      {options.map((option) => (
        <Button
          key={option.label}
          size="sm"
          aria-pressed={selected === option.id}
          onClick={() => onSelect(option.id)}
          className={
            selected === option.id
              ? "shrink-0 gap-2 rounded-full border-purple-200 bg-purple-50 text-purple-900 hover:bg-purple-100"
              : "shrink-0 gap-2 rounded-full"
          }
        >
          {option.label}
          <span className="text-text-tertiary tabular-nums">
            {countOpenGoals(items, option.id)}
          </span>
        </Button>
      ))}
    </div>
  );
}

function RouteComponent() {
  const keyManager = useUnlockedKey();
  const [timeline, setTimeline] = useState<GoalTimeline | undefined>();

  const { data: rows, isLoading, isError, refetch } = useGoalsOfLife();
  const { data: sharedStatus } = useUserSharedStatus();
  const shareWithCoaches = sharedStatus?.isSharedGoalsWithCoach ?? false;

  const items = useMemo(() => goalItems(rows ?? []), [rows]);
  const groups = useMemo(
    () =>
      groupGoals(
        timeline
          ? items.filter((item) => item.goal.timeline === timeline)
          : items,
      ),
    [items, timeline],
  );
  const { openGoal, openNewGoal, managePublicLink, panels } = useGoalPanels({
    items,
    keyManager,
    isSharedWithCoaches: shareWithCoaches,
  });

  const renderContent = () => {
    if (isLoading) return <GoalsSkeleton />;
    if (isError) {
      return (
        <ErrorStateCard
          title="Your goals did not load"
          message="Check your connection and try again."
          action={<Button onClick={() => void refetch()}>Try again</Button>}
        />
      );
    }
    if (items.length === 0) {
      return <GoalsEmptyState onCreate={() => openNewGoal()} />;
    }
    if (groups.categories.length === 0 && groups.done.length === 0) {
      return (
        <EmptyState
          icon={Search}
          size="sm"
          title="No goals with this timeframe"
        >
          <Button onClick={() => setTimeline(undefined)}>Show all</Button>
        </EmptyState>
      );
    }
    return (
      <GoalList
        groups={groups}
        keyManager={keyManager}
        showPublicLink
        onOpen={(item) => openGoal(item.goal.id)}
        onAddGoal={openNewGoal}
      />
    );
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <h2 className="flex items-center gap-2 font-heading text-xl font-medium text-gray-900">
            Goals
            <EncryptionBadge variant={shareWithCoaches ? "shared" : "user"} />
          </h2>
          <p className="text-text-secondary text-sm">
            What you want to reach, and the steps that get you there.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SharingMenu
            items={items}
            keyManager={keyManager}
            onManageLink={managePublicLink}
          />
          {items.length > 0 && (
            <Button variant="primary" onClick={() => openNewGoal()}>
              <Plus aria-hidden />
              New goal
            </Button>
          )}
        </div>
      </header>

      {items.length > 0 && (
        <TimeframeChips
          items={items}
          selected={timeline}
          onSelect={setTimeline}
        />
      )}

      {renderContent()}

      {panels}
    </div>
  );
}
